import { useMemo } from "react";
import {
  Alert,
  Anchor,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { Link } from "react-router-dom";
import type { LegacyCaseDetail } from "../../../types/legacyCases";
import type { LegacyReviewValues } from "../../../utils/legacyReviewForm";
import type { LegacyPatientCandidate } from "../../../utils/legacyPatientMapping";
import { legacyPatientComparison } from "../../../utils/legacyPatientComparison";

interface Props {
  detail: LegacyCaseDetail;
  values: LegacyReviewValues;
  patient: LegacyPatientCandidate;
}

export default function LegacyPatientIdentityComparison({
  detail,
  values,
  patient,
}: Props) {
  const fields = useMemo(
    () => legacyPatientComparison(detail, values, patient),
    [detail, patient, values]
  );
  const differences = useMemo(
    () =>
      fields
        .filter((field) => field.differs)
        .map((field) => field.label.toLowerCase()),
    [fields]
  );
  const source = useMemo(
    () => detail.sourceRows.find((row) => row.id === values.sourceRowId),
    [detail.sourceRows, values.sourceRowId]
  );
  const report = useMemo(
    () =>
      detail.documents.find(
        (document) => document.id === values.reportDocumentId
      ),
    [detail.documents, values.reportDocumentId]
  );
  return (
    <Paper withBorder p="sm" style={{ overflowWrap: "anywhere" }}>
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>Compare patient identity</Text>
          <Anchor
            component={Link}
            to={`/cases?patientIds=${encodeURIComponent(patient.id)}`}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
          >
            View this patient's cases
          </Anchor>
        </Group>
        <Text size="xs" c="dimmed">
          Spreadsheet:{" "}
          {source
            ? `${source.sourceFileName} · ${source.sheetName} · row ${source.rowNumber}`
            : "No source selected"}
          <br />
          Final report: {report?.originalFileName || "No report selected"}
        </Text>
        {differences.length > 0 && (
          <Alert color="orange" title="Identity differences need review">
            The historical evidence and current patient differ in{" "}
            {differences.join(", ")}. Changes such as a married name can be
            valid. Confirm they refer to the same person before choosing Use
            this patient. Current LIS demographics and the historical report
            will stay unchanged.
          </Alert>
        )}
        {fields.map((field) => (
          <Stack gap={4} key={field.field}>
            <Divider />
            <Text size="sm" fw={600}>
              {field.label}
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Historical evidence
                </Text>
                <Text size="sm">
                  Spreadsheet: {field.spreadsheet || "Not provided"}
                </Text>
                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                  Report: {field.reportValues.join(" / ") || "Not identified"}
                </Text>
              </Stack>
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Selected existing patient
                </Text>
                <Text size="sm" fw={500}>
                  {field.current || "Not provided"}
                </Text>
              </Stack>
            </SimpleGrid>
          </Stack>
        ))}
        <Text size="sm" c="dimmed">
          Suggestion: {patient.matchReason}. Suggestions do not confirm
          identity.
        </Text>
      </Stack>
    </Paper>
  );
}
